import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiPost, apiGet } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import Logo from '../components/Logo.jsx';
import ApprovalWaitingScreen from '../components/onboarding/ApprovalWaitingScreen.jsx';
import A2pCompliancePanel, { DEFAULT_A2P_PASSCODE } from '../components/signup/A2pCompliancePanel.jsx';
import {
  Sparkles, Shield, Zap, Phone, MessageSquare, Check, Loader2,
  ArrowRight, Lock, Mail, User as UserIcon, KeyRound, MapPin, ChevronDown, X,
} from 'lucide-react';

const ALL_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','D.C.'],['FL','Florida'],
  ['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],
  ['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],
  ['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

/**
 * Public signup page. Optional A2P passcode or manager invite token/ref.
 * Shareable invite link format: /signup?ref=<inviterId>&token=<token>
 */
export default function Signup() {
  const [params] = useSearchParams();
  // ?a2p= is embedded in invite links; ?code= is the legacy manual passcode link.
  const inviteCode = params.get('a2p') || params.get('code') || '';
  const inviteFrom = params.get('from') || '';
  const refParam = params.get('ref') || '';
  const tokenParam = params.get('token') || '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Locked field: invite code, else the master agency profile so direct signups are compliant too.
  const [a2pPasscode, setA2pPasscode] = useState(inviteCode || DEFAULT_A2P_PASSCODE);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [licensedStates, setLicensedStates] = useState([]);
  const [stateDropOpen, setStateDropOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const [inviterInfo, setInviterInfo] = useState(null);
  const [isPendingApproval, setIsPendingApproval] = useState(false);

  const { setSession, agent } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (inviteCode) setA2pPasscode(inviteCode);
  }, [inviteCode]);

  useEffect(() => {
    if (tokenParam) {
      apiGet(`/team/invites/${tokenParam}`)
        .then((res) => {
          if (res?.ok && res.invite) {
            if (res.invite.email) setEmail(res.invite.email);
            if (res.invite.name) setName(res.invite.name);
            // Authoritative even if the query string was truncated.
            if (res.invite.a2pPasscode) setA2pPasscode(res.invite.a2pPasscode);
            setInviterInfo(res.invite);
          }
        })
        .catch(() => {});
    }
  }, [tokenParam]);

  // If already logged in and pending approval, show waiting screen
  if (isPendingApproval || (agent && agent.status === 'pending_approval')) {
    return (
      <main className="min-h-screen bg-[#0A0A0F] text-white flex items-center justify-center p-4">
        <ApprovalWaitingScreen onApproved={() => nav('/phone-setup', { replace: true })} />
      </main>
    );
  }

  async function submit(e) {
    e.preventDefault();
    if (!agreed) {
      toast.error('Please agree to the Terms and Privacy Policy.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    const isInvited = !!(tokenParam || refParam);
    if (!isInvited && !licensedStates.length) {
      toast.error('Please select at least one licensed state.');
      return;
    }
    setLoading(true);
    try {
      const r = await apiPost('/auth/register', {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        a2pPasscode: a2pPasscode.trim() || undefined,
        inviteToken: tokenParam || undefined,
        ref: refParam || undefined,
        licensedStates: licensedStates.length ? licensedStates : ['CO'],
      });
      if (!r || !r.token) throw new Error('No token returned');
      setSession(r.token, r.agent);

      if (r.agent?.status === 'pending_approval') {
        setIsPendingApproval(true);
        toast('Registration submitted! Awaiting manager approval.', { icon: '⏳' });
        return;
      }

      toast.success('Welcome to CIVIX');
      // New accounts land on Phone Setup (never the dashboard); the agreement is signed there.
      setTimeout(() => nav('/phone-setup', { replace: true }), 50);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Signup failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#0A0A0F] text-white relative overflow-x-hidden">
      {/* Ambient gradients */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
             style={{ background: 'radial-gradient(circle, #7B2FBE 0%, transparent 70%)' }} />
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full opacity-25 blur-3xl"
             style={{ background: 'radial-gradient(circle, #3B82F6 0%, transparent 70%)' }} />
        <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full opacity-15 blur-3xl"
             style={{ background: 'radial-gradient(circle, #E040FB 0%, transparent 70%)' }} />
      </div>

      {/* Subtle grid */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Top nav */}
      <header className="relative z-10 border-b border-white/5 backdrop-blur-xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-3 hover:opacity-80 transition shrink-0">
            <Logo size="sm" showTagline={false} />
            <span className="hidden md:inline-block text-[10px] tracking-widest uppercase text-white/40 ml-1">AI-Powered CRM</span>
          </Link>
          <Link to="/login" className="text-sm text-white/70 hover:text-white transition whitespace-nowrap shrink-0">
            <span className="hidden sm:inline">Already have an account? </span>
            <span className="text-white font-medium">Sign in</span>
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left — pitch */}
          <div className="hidden lg:block space-y-8 sticky top-24">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border border-white/10 bg-white/2 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[#E040FB]" />
                <span className="text-white/70">Free during beta · Founding member access</span>
              </div>
              <h1 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1] xl:leading-none mb-4">
                Start closing more policies{' '}
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #60A5FA 0%, #E040FB 100%)' }}
                >
                  with AI that actually works.
                </span>
              </h1>
              <p className="text-white/60 text-lg leading-relaxed">
                Your account is ready in 60 seconds. No card required during beta. Bring your own Twilio + carriers — we run at-cost and never mark up usage.
              </p>
            </div>

            <div className="space-y-3">
              {[
                { icon: Phone, title: 'Power Dialer', desc: 'Multi-line dialing with call recording + AI call summaries on every completed call.' },
                { icon: MessageSquare, title: 'Two-way SMS + iMessage', desc: 'Twilio & SendBlue drips, keywords, and auto-replies in one unified inbox.' },
                { icon: Shield, title: 'Built-in Compliance', desc: 'A2P 10DLC brand signature, TCPA send-window enforcement, STOP opt-out & internal DNC suppression.' },
                { icon: Zap, title: 'Underwriting Bot', desc: 'Trained only on your carrier grids — never the open web.' },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-white/2 hover:bg-white/4 transition">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                       style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(236,72,153,0.2))', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <f.icon className="w-4 h-4 text-white/90" />
                  </div>
                  <div>
                    <div className="font-medium text-white/95 text-sm mb-0.5">{f.title}</div>
                    <div className="text-xs text-white/55">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 text-xs text-white/45 pt-4">
              <Lock className="w-3.5 h-3.5" />
              <span>Multi-tenant isolation · 2FA &amp; audit log · Your own Twilio, billed at-cost</span>
            </div>
          </div>

          {/* Right — form card */}
          <div className="relative">
            {/* Card glow */}
            <div className="absolute -inset-1 rounded-2xl opacity-50 blur-xl pointer-events-none"
                 style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.4), rgba(236,72,153,0.3), rgba(59,130,246,0.3))' }} />

            <div className="relative rounded-2xl border border-white/10 bg-[#0D0D1A]/90 backdrop-blur-xl p-7 lg:p-9 shadow-2xl">
              {inviteFrom && (
                <div className="mb-6 p-4 rounded-xl border bg-linear-to-r from-[#7B2FBE]/15 to-[#E040FB]/10"
                     style={{ borderColor: 'rgba(224,64,251,0.3)' }}>
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-[#E040FB] shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-white">You've been invited by {inviteFrom}</div>
                      <div className="text-xs text-white/55 mt-1">
                        Their team passcode is pre-filled below. You'll get instant SMS + calling access under their A2P brand.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <h2 className="font-display text-2xl lg:text-3xl font-bold mb-1">Create your account</h2>
              {inviterInfo ? (
                <div className="mb-6 p-3 rounded-lg bg-civix-purple/15 border border-civix-purple/30 text-xs text-civix-cyan flex items-center gap-2">
                  <Sparkles className="w-4 h-4 shrink-0 text-civix-purple" />
                  <span>
                    You've been invited to join <strong>{inviterInfo.tenantName || 'CIVIX'}</strong>. Complete your signup below.
                  </span>
                </div>
              ) : (
                <p className="text-white/55 text-sm mb-7">It's free during the beta. No credit card required.</p>
              )}

              <form onSubmit={submit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/50 font-medium">Full name</label>
                  <div className="relative mt-1.5">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                    <input
                      type="text"
                      required
                      autoComplete="off"
                      name="signup-fullname"
                      data-lpignore="true"
                      data-form-type="other"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Theodore Roosevelt"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-white/3 border border-white/10 focus:border-civix-purple focus:bg-white/5 outline-hidden transition text-sm"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/50 font-medium">Work email</label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@agency.com"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-white/3 border border-white/10 focus:border-civix-purple focus:bg-white/5 outline-hidden transition text-sm"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/50 font-medium">Password</label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                    <input
                      type="password"
                      required
                      autoComplete="new-password"
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-white/3 border border-white/10 focus:border-civix-purple focus:bg-white/5 outline-hidden transition text-sm"
                    />
                  </div>
                  <div className="text-[11px] text-white/40 mt-1.5">8+ characters. Mix in numbers and symbols for safety.</div>
                </div>

                {/* A2P compliance code: pre-embedded and locked */}
                <A2pCompliancePanel passcode={a2pPasscode} />

                {/* Licensed States — REQUIRED */}
                <div className="relative">
                  <label className="text-xs uppercase tracking-wider text-white/50 font-medium flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    Licensed States
                    <span className="text-[#E040FB] ml-0.5">*</span>
                  </label>

                  {/* Selected pills */}
                  <div
                    className="mt-1.5 min-h-[44px] w-full rounded-lg bg-white/3 border border-white/10 focus-within:border-civix-purple transition cursor-pointer px-2 py-1.5 flex flex-wrap gap-1 items-center"
                    onClick={() => setStateDropOpen((o) => !o)}
                  >
                    {licensedStates.length === 0 && (
                      <span className="text-sm text-white/30 px-1">Select states where you hold a license…</span>
                    )}
                    {licensedStates.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-[#7C3AED]/30 border border-[#7C3AED]/50 text-white"
                      >
                        {s}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setLicensedStates((prev) => prev.filter((x) => x !== s)); }}
                          className="hover:text-red-400 transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <ChevronDown className={`w-4 h-4 text-white/30 ml-auto shrink-0 transition-transform ${stateDropOpen ? 'rotate-180' : ''}`} />
                  </div>

                  {/* Dropdown */}
                  {stateDropOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/10 bg-[#0D0D1A] shadow-xl max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-white/10">
                        <input
                          type="text"
                          autoFocus
                          value={stateSearch}
                          onChange={(e) => setStateSearch(e.target.value)}
                          placeholder="Search states…"
                          className="w-full text-sm bg-white/5 border border-white/10 rounded-sm px-3 py-1.5 outline-hidden text-white placeholder-white/30"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <ul className="overflow-y-auto max-h-44">
                        {ALL_STATES.filter(([code, name]) =>
                          !stateSearch ||
                          code.toLowerCase().includes(stateSearch.toLowerCase()) ||
                          name.toLowerCase().includes(stateSearch.toLowerCase())
                        ).map(([code, label]) => (
                          <li
                            key={code}
                            className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-white/5 transition ${
                              licensedStates.includes(code) ? 'text-civix-purpleSoft' : 'text-white/80'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLicensedStates((prev) =>
                                prev.includes(code)
                                  ? prev.filter((x) => x !== code)
                                  : [...prev, code]
                              );
                            }}
                          >
                            <span className={`w-3 h-3 rounded-xs border flex items-center justify-center shrink-0 ${
                              licensedStates.includes(code)
                                ? 'bg-[#7C3AED] border-[#7C3AED]'
                                : 'border-white/30'
                            }`}>
                              {licensedStates.includes(code) && <Check className="w-2.5 h-2.5 text-white" />}
                            </span>
                            <span className="font-medium w-8">{code}</span>
                            <span className="text-white/50">{label}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="border-t border-white/10 px-3 py-2 flex gap-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); setLicensedStates(ALL_STATES.map(([c]) => c)); setStateDropOpen(false); }}
                          className="text-xs text-white/50 hover:text-white transition">Select all</button>
                        <span className="text-white/20">·</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setLicensedStates([]); }}
                          className="text-xs text-white/50 hover:text-white transition">Clear</button>
                        <span className="ml-auto text-xs text-white/30">{licensedStates.length} selected</span>
                      </div>
                    </div>
                  )}

                  <div className="text-[11px] text-white/40 mt-1">
                    Required for lead routing. You can update this in Settings later.
                  </div>
                </div>

                {/* Terms */}
                <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded-sm border-white/20 bg-white/3 accent-civix-purple"
                  />
                  <span className="text-xs text-white/55 leading-relaxed">
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-white/85 hover:text-white underline underline-offset-2">Terms of Service</Link>{' '}
                    and{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-white/85 hover:text-white underline underline-offset-2">Privacy Policy</Link>
                    , including TCPA-compliant outreach and STOP opt-out handling.
                  </span>
                </label>

                {/* CTA */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-2 py-3 px-4 rounded-lg font-semibold text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
                    boxShadow: '0 10px 40px -10px rgba(124,58,237,0.6)',
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Creating your workspace…</span>
                    </>
                  ) : (
                    <>
                      <span>Create my CIVIX account</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                {/* Submit disclosure (required for Twilio/Meta reviewer evidence) */}
                <p className="text-[11px] text-white/45 text-center leading-relaxed">
                  By signing up you agree to our{' '}
                  <Link to="/terms" className="text-white/75 hover:text-white underline underline-offset-2">Terms of Service</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-white/75 hover:text-white underline underline-offset-2">Privacy Policy</Link>.
                </p>

                {/* Trust row */}
                <div className="grid grid-cols-3 gap-2 pt-3 text-center">
                  {['Free during beta', 'No card required', 'Cancel anytime'].map((t) => (
                    <div key={t} className="flex items-center justify-center gap-1.5 text-[11px] text-white/45">
                      <Check className="w-3 h-3 text-emerald-400" />
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </form>

              {/* Footer link */}
              <div className="mt-6 pt-5 border-t border-white/5 text-center text-sm text-white/55">
                Already have an account?{' '}
                <Link to="/login" className="text-white font-medium hover:text-white/80 transition">
                  Sign in
                </Link>
              </div>
            </div>

            {/* Mobile-only feature strip */}
            <div className="lg:hidden mt-8 grid grid-cols-2 gap-3">
              {[
                { icon: Phone, label: 'Power Dialer' },
                { icon: Shield, label: 'A2P Compliance' },
                { icon: MessageSquare, label: 'Two-way SMS' },
                { icon: Zap, label: 'Underwriting Bot' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2.5 p-3 rounded-lg border border-white/5 bg-white/2">
                  <f.icon className="w-4 h-4 text-white/70" />
                  <span className="text-xs text-white/75">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <div>© {new Date().getFullYear()} CIVIX LLC. Built for life-insurance teams.</div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white/70">Privacy</Link>
            <Link to="/terms" className="hover:text-white/70">Terms</Link>
            <Link to="/sms-policy" className="hover:text-white/70">SMS Policy</Link>
            <a href="mailto:ceo@civixcrm.ai" className="hover:text-white/70">Support</a>
          </div>
        </div>
      </footer>
    </main>
  );
}
